import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Transition } from '@uirouter/core';

import { Analytics, CimaCore, Cloudinary, Util, XmStore, XmStoreUtil } from 'core/services';
import { CmsCore } from 'store/cms/models';
import { CONFIG_TOKEN, TransitionHooks } from 'core/constants';
import { IXMOptions } from 'core/interfaces';
import { PAGE_LANGUAGE } from 'services/analytics/digital-data/constants';

@Component({
    selector: 'xm-footer',
    styleUrls: ['./footer.scss'],
    templateUrl: './footer.html',
    standalone: false
})
export class XmFooterComponent implements OnInit, OnDestroy, AfterViewInit {
    public pageData: SiteFooter;
    public copyrightText: string;

    private xmStore: XmStore;
    private cimaCore: CimaCore;
    private subscriptions: Subscription[] = [];
    private analytics: Analytics;

    private observer: MutationObserver;

    constructor(element: ElementRef, xmStore: XmStore, cimaCore: CimaCore, @Inject(CONFIG_TOKEN) config: IXMOptions, @Inject(TransitionHooks.SUCCESS) transitionSuccess: Observable<Transition>, @Inject(Analytics) analytics: Analytics) {
        Object.assign(this, { footerElement: element.nativeElement, xmStore, cimaCore, config, transitionSuccess, analytics });
    }

    public ngOnInit(): void {
        this.subscriptions.push(XmStoreUtil.subscribe(this.xmStore.peek<CmsCore>(CmsCore), (result: CmsCore) => {
            this.pageData = result.footer;
        }));

        this.copyrightText = this.setCopyrightYear();
    }

    public ngAfterViewInit(): void {
        const browserLanguage: string = window.navigator.language;
        const isSpanish: boolean = browserLanguage.startsWith('es');
        if (isSpanish && window.lzSwitchLanguage) {
            const defaultLanguage: string = PAGE_LANGUAGE.spanish;
            window.lzSwitchLanguage(defaultLanguage).then(() => {
                this.sendEventToDataLayer(defaultLanguage);
            });
        }
    }

    public get isBusinessUser(): boolean {
        return this.cimaCore.isBusinessUser;
    }

    public get footerLinks(): CmsSiteFooterLink[] {
        return this.cimaCore.isBusinessUser ? this.pageData.businessLinks : this.pageData.residentialLinks;
    }

    public buildIcon(icon: CmsMediaImage): MediaImageOptions {
        return { mobile: Cloudinary.generateMediaFromCms(icon) };
    }

    public onLanguageChange(selectedLanguage: string): void {
        if (window.lzSwitchLanguage) {
            if (selectedLanguage === PAGE_LANGUAGE.english || selectedLanguage === PAGE_LANGUAGE.spanish) {
                window.lzSwitchLanguage(selectedLanguage).then(() => {
                    this.sendEventToDataLayer(selectedLanguage);
                });
            }
        }
    }

    public ngOnDestroy(): void {
        Util.unsubscribeAll(this.subscriptions);
        this.observer?.disconnect();
    }

    private setCopyrightYear(): string {
        return this.isBusinessUser ? this.pageData.copyrightForBusiness.replace('[DATE]', new Date().getFullYear().toString()) : this.pageData.copyright.replace('[DATE]', new Date().getFullYear().toString());
    }

    private sendEventToDataLayer(language: string): void {
        this.analytics.sendCustomEvent('letzchat-translation-footer-click', this.createCustomEventParam(language), true);
    }

    private createCustomEventParam(language: string): object {
        return {
            page: {
                pageInfo: {
                    language: language
                }
            }
        };
    }

}
